<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportMetricCard from './ReportMetricCard.vue';
import ReportsAPI from 'dashboard/api/reports';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const conversationCount = ref(0);
const aiReplyCount = ref(0);
const messagePartsCount = ref(0);
const resolutionRate = ref(0);
const handoffRate = ref(0);
const quotaUsage = ref('--');
const quotaReset = ref('--');
const { t } = useI18n();

const formatToPercent = value => {
  if (value === null || value === undefined) {
    return '--';
  }

  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return '--';
  }

  return `${numberValue}%`;
};

const formatNumber = value => {
  if (value === null || value === undefined) {
    return '--';
  }

  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return '--';
  }

  return numberValue.toLocaleString();
};

const formatQuotaReset = value => {
  if (!value) {
    return '--';
  }
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return '--';
  }
};

const fetchMetrics = () => {
  if (!props.filters.to || !props.filters.from) {
    return;
  }
  ReportsAPI.getBotMetrics(props.filters).then(response => {
    const data = response.data;
    conversationCount.value = Number(data.conversation_count || 0);
    aiReplyCount.value = Number(data.message_count || 0);
    messagePartsCount.value = Number(data.message_parts_count || 0);
    resolutionRate.value = Number(data.resolution_rate || 0);
    handoffRate.value = Number(data.handoff_rate || 0);

    if (data.ai_reply_quota_unlimited) {
      quotaUsage.value = t('BOT_REPORTS.METRIC.QUOTA_USAGE.UNLIMITED');
    } else if (data.ai_reply_quota_limit) {
      const used = formatNumber(data.ai_reply_quota_used);
      const limit = formatNumber(data.ai_reply_quota_limit);
      const percent = data.ai_reply_quota_usage_percent
        ? formatToPercent(data.ai_reply_quota_usage_percent)
        : '';
      quotaUsage.value = percent ? `${used}/${limit} (${percent})` : `${used}/${limit}`;
    } else {
      quotaUsage.value = '--';
    }

    quotaReset.value = formatQuotaReset(data.ai_reply_quota_resets_at);
  });
};

watch(() => props.filters, fetchMetrics, { deep: true });

onMounted(fetchMetrics);

const activityMetrics = computed(() => [
  {
    key: 'bot-conversations',
    label: t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP'),
    value: formatNumber(conversationCount.value),
  },
  {
    key: 'bot-ai-replies',
    label: t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP'),
    value: formatNumber(aiReplyCount.value),
  },
  {
    key: 'bot-message-parts',
    label: t('BOT_REPORTS.METRIC.TOTAL_MESSAGE_PARTS.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.TOTAL_MESSAGE_PARTS.TOOLTIP'),
    value: formatNumber(messagePartsCount.value),
  },
  {
    key: 'bot-resolution-rate',
    label: t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.RESOLUTION_RATE.TOOLTIP'),
    value: formatToPercent(resolutionRate.value),
  },
  {
    key: 'bot-handoff-rate',
    label: t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.HANDOFF_RATE.TOOLTIP'),
    value: formatToPercent(handoffRate.value),
  },
]);

const quotaMetrics = computed(() => [
  {
    key: 'bot-quota-usage',
    label: t('BOT_REPORTS.METRIC.QUOTA_USAGE.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.QUOTA_USAGE.TOOLTIP'),
    value: quotaUsage.value,
  },
  {
    key: 'bot-quota-reset',
    label: t('BOT_REPORTS.METRIC.QUOTA_RESET.LABEL'),
    tooltip: t('BOT_REPORTS.METRIC.QUOTA_RESET.TOOLTIP'),
    value: quotaReset.value,
  },
]);
</script>

<template>
  <div
    class="space-y-6 mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-6"
  >
    <section>
      <div class="mb-3">
        <h3 class="text-md font-semibold text-n-slate-12">
          {{ $t('BOT_REPORTS.SECTIONS.ACTIVITY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('BOT_REPORTS.SECTIONS.ACTIVITY.DESCRIPTION') }}
        </p>
      </div>
      <div class="grid gap-4 md:grid-cols-2">
        <ReportMetricCard
          v-for="metric in activityMetrics"
          :key="metric.key"
          :label="metric.label"
          :info-text="metric.tooltip"
          :value="metric.value"
        />
      </div>
    </section>

    <section>
      <div class="mb-3">
        <h3 class="text-md font-semibold text-n-slate-12">
          {{ $t('BOT_REPORTS.SECTIONS.QUOTA.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('BOT_REPORTS.SECTIONS.QUOTA.DESCRIPTION') }}
        </p>
      </div>
      <div class="grid gap-4 md:grid-cols-2">
        <ReportMetricCard
          v-for="metric in quotaMetrics"
          :key="metric.key"
          :label="metric.label"
          :info-text="metric.tooltip"
          :value="metric.value"
        />
      </div>
    </section>
  </div>
</template>
